// src/hooks/useStreamingSocket.ts
//
// 可复用的流式 WebSocket Hook：连接管理 + 去重 + 消息分发
//
// 设计要点（即此前修复语音丢消息的根因方案）：
// 早期实现用单个 `lastMessage` 状态消费消息，当服务端按句密集推送
// `tts_start → tts_chunk → tts_complete` 时，React 会合并多次渲染、只跑
// 一次 useEffect，导致中间的 tts_chunk 被静默丢弃，语音永远不播放。
//
// 本 Hook 用两种手段彻底消除该问题：
//   1. 维护 messages 数组（functional setState 累积，不受批渲染影响）；
//   2. 在 onmessage 中【同步】调用订阅者，绕开 React 状态/效应调度，
//      保证每条去重后的消息都立即、且仅一次送达订阅者。
//
// 用法：
//   const { isConnected, sendMessage, subscribe } = useStreamingSocket(wsUrl);
//   useEffect(() => subscribe((msg) => {
//       switch (msg.type) { ... }   // 在此处理业务消息
//   }), [subscribe]);
//
// 注意：subscribe 返回的取消函数应在 useEffect 清理时调用。订阅回调在
// WebSocket 事件中同步执行，若需要读取组件响应式状态，请通过 ref 读取
// 或在依赖变化时重新订阅，避免闭包过期。

import { useCallback, useEffect, useRef, useState } from "react";
import type { WebSocketMessage } from "../types";
import { CONSTANTS, WS_CONFIG } from "../utils/constants";
import { generateUniqueId } from "../utils/idGenerator";

/**
 * 模块级连接：React Strict Mode 会立刻卸载/重挂 hook，
 * 若在 CONNECTING 时 close()，浏览器会报
 * "WebSocket is closed before the connection is established"。
 */
let sharedWs: WebSocket | null = null;
let sharedRetain = 0;
let sharedCloseTimer: number | null = null;

/** 消息处理回调：每条去重后的服务端消息都会同步回调一次 */
export type SocketMessageHandler = (msg: WebSocketMessage) => void;

export interface UseStreamingSocketOptions {
    /** WebSocket 地址，缺省读取 VITE_WS_URL */
    url?: string;
    /** 心跳间隔（ms），缺省走 WS_CONFIG.HEARTBEAT_INTERVAL */
    heartbeatInterval?: number;
    /** 最大重连次数，缺省走 WS_CONFIG.RECONNECT_ATTEMPTS */
    reconnectAttempts?: number;
    /** 重连退避（ms），缺省走 WS_CONFIG.RECONNECT_DELAY */
    reconnectDelay?: number;
    /** 历史消息保留条数，缺省走 CONSTANTS.MAX_MESSAGE_HISTORY */
    maxHistory?: number;
}

export interface UseStreamingSocketReturn {
    /** 是否已连接 */
    isConnected: boolean;
    /** 当前重连次数 */
    reconnectAttempts: number;
    /** 是否已用尽自动重连 */
    reconnectExhausted: boolean;
    maxReconnectAttempts: number;
    /** 历史消息（去重后累积，超过 maxHistory 会从头裁剪） */
    messages: WebSocketMessage[];
    /** 发送消息，返回是否成功投递 */
    sendMessage: (message: WebSocketMessage) => boolean;
    /** 订阅服务端消息，返回取消订阅函数 */
    subscribe: (handler: SocketMessageHandler) => () => void;
    /** 清空历史消息与去重表 */
    clearMessages: () => void;
    /** 主动重连（重置计数） */
    reconnect: () => void;
}

/**
 * 流式 WebSocket Hook：连接管理 + 去重 + 订阅分发。
 * @param options 配置对象，或直接传 URL 字符串
 */
export const useStreamingSocket = (
    options: UseStreamingSocketOptions | string = {},
): UseStreamingSocketReturn => {
    const opts: UseStreamingSocketOptions =
        typeof options === "string" ? { url: options } : options;

    const wsUrl = opts.url || WS_CONFIG.URL;
    const heartbeatInterval =
        opts.heartbeatInterval ?? WS_CONFIG.HEARTBEAT_INTERVAL ?? 30000;
    const maxReconnectAttempts =
        opts.reconnectAttempts ?? WS_CONFIG.RECONNECT_ATTEMPTS ?? 5;
    const reconnectDelay = opts.reconnectDelay ?? WS_CONFIG.RECONNECT_DELAY ?? 3000;
    const maxHistory = opts.maxHistory ?? CONSTANTS.MAX_MESSAGE_HISTORY ?? 100;

    const [isConnected, setIsConnected] = useState(false);
    const [reconnectCount, setReconnectAttempts] = useState(0);
    const [messages, setMessages] = useState<WebSocketMessage[]>([]);

    // 用 ref 保存最新的 socket，避免 useEffect cleanup 闭包捕获到过期的 null
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimerRef = useRef<number | null>(null);
    const pingIntervalRef = useRef<number | null>(null);
    // 去重表：同一 id 的消息只处理一次
    const messageIdsRef = useRef<Set<string>>(new Set());
    // 标记是否为主动关闭，避免卸载时触发重连
    const manualCloseRef = useRef(false);
    // 订阅者集合：用 ref 持有，onmessage 中同步遍历调用
    const subscribersRef = useRef<Set<SocketMessageHandler>>(new Set());

    const clearTimers = useCallback(() => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    }, []);

    // 同步分发：在 onmessage 中直接调用所有订阅者，绕开 React 批渲染
    const dispatch = useCallback((msg: WebSocketMessage) => {
        subscribersRef.current.forEach((handler) => {
            try {
                handler(msg);
            } catch (err) {
                console.error("❌ 订阅者处理消息异常:", err);
            }
        });
    }, []);

    const connect = useCallback(() => {
        try {
            const bindHandlers = (ws: WebSocket) => {
                ws.onopen = () => {
                    if (sharedWs !== ws) return;
                    console.log("✅ WebSocket connected successfully");
                    setIsConnected(true);
                    reconnectAttemptsRef.current = 0;
                    setReconnectAttempts(0);
                    messageIdsRef.current.clear();

                    if (pingIntervalRef.current) {
                        clearInterval(pingIntervalRef.current);
                    }
                    pingIntervalRef.current = window.setInterval(() => {
                        if (ws.readyState === WebSocket.OPEN) {
                            ws.send(
                                JSON.stringify({
                                    type: "ping",
                                    timestamp: Date.now(),
                                }),
                            );
                        }
                    }, heartbeatInterval);
                };

                ws.onmessage = (event) => {
                    try {
                        const data: WebSocketMessage = JSON.parse(
                            event.data,
                        ) as WebSocketMessage;
                        console.log("📨 Received:", data.type, data);

                        if (data.type === "pong") {
                            return;
                        }

                        const messageId = data.id || generateUniqueId();

                        if (messageIdsRef.current.has(messageId)) {
                            return;
                        }
                        messageIdsRef.current.add(messageId);

                        const newMessage: WebSocketMessage = {
                            ...data,
                            id: messageId,
                            timestamp: data.timestamp || Date.now(),
                        };

                        setMessages((prev) => {
                            const next = [...prev, newMessage];
                            if (next.length > maxHistory) {
                                const removed = next.shift();
                                if (removed?.id) {
                                    messageIdsRef.current.delete(removed.id);
                                }
                            }
                            return next;
                        });

                        dispatch(newMessage);
                    } catch (error) {
                        console.error(
                            "❌ Failed to parse WebSocket message:",
                            error,
                        );
                    }
                };

                ws.onclose = (event) => {
                    if (sharedWs === ws) {
                        sharedWs = null;
                    }
                    if (socketRef.current === ws) {
                        socketRef.current = null;
                    }
                    setIsConnected(false);

                    if (pingIntervalRef.current) {
                        clearInterval(pingIntervalRef.current);
                        pingIntervalRef.current = null;
                    }

                    if (manualCloseRef.current) {
                        return;
                    }

                    console.log(
                        `⚠️ WebSocket disconnected: ${event.code} - ${event.reason || "No reason"}`,
                    );

                    if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                        reconnectAttemptsRef.current++;
                        setReconnectAttempts(reconnectAttemptsRef.current);
                        console.log(
                            `🔄 Reconnecting attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${reconnectDelay}ms`,
                        );
                        reconnectTimerRef.current = window.setTimeout(() => {
                            connect();
                        }, reconnectDelay);
                    } else {
                        setReconnectAttempts(reconnectAttemptsRef.current);
                        console.error("❌ Max reconnection attempts reached");
                    }
                };

                ws.onerror = () => {
                    if (manualCloseRef.current) return;
                    if (
                        ws.readyState === WebSocket.CONNECTING ||
                        ws.readyState === WebSocket.CLOSING
                    ) {
                        return;
                    }
                    console.error("❌ WebSocket error");
                };
            };

            const existing = sharedWs ?? socketRef.current;
            if (
                existing &&
                (existing.readyState === WebSocket.OPEN ||
                    existing.readyState === WebSocket.CONNECTING)
            ) {
                socketRef.current = existing;
                sharedWs = existing;
                bindHandlers(existing);
                if (existing.readyState === WebSocket.OPEN) {
                    setIsConnected(true);
                }
                return;
            }

            console.log(`🔌 Connecting to WebSocket: ${wsUrl}`);
            manualCloseRef.current = false;
            const ws = new WebSocket(wsUrl);
            socketRef.current = ws;
            sharedWs = ws;
            bindHandlers(ws);
        } catch (error) {
            console.error("❌ Failed to create WebSocket connection:", error);
        }
    }, [
        wsUrl,
        heartbeatInterval,
        maxReconnectAttempts,
        reconnectDelay,
        maxHistory,
        dispatch,
    ]);

    useEffect(() => {
        if (sharedCloseTimer != null) {
            window.clearTimeout(sharedCloseTimer);
            sharedCloseTimer = null;
        }
        sharedRetain += 1;
        connect();

        return () => {
            sharedRetain = Math.max(0, sharedRetain - 1);
            const ws = sharedWs ?? socketRef.current;
            sharedCloseTimer = window.setTimeout(() => {
                sharedCloseTimer = null;
                if (sharedRetain > 0) return;
                manualCloseRef.current = true;
                clearTimers();
                if (!ws) return;
                ws.onerror = null;
                ws.onclose = null;
                if (ws.readyState === WebSocket.OPEN) {
                    ws.close();
                } else if (ws.readyState === WebSocket.CONNECTING) {
                    ws.onopen = () => {
                        try {
                            ws.close();
                        } catch {
                            /* ignore */
                        }
                    };
                }
                if (sharedWs === ws) sharedWs = null;
                if (socketRef.current === ws) socketRef.current = null;
            }, 80);
        };
    }, [connect, clearTimers]);

    const sendMessage = useCallback((message: WebSocketMessage): boolean => {
        const ws = socketRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
            const messageWithId = {
                ...message,
                id: message.id || generateUniqueId(),
                timestamp: message.timestamp || Date.now(),
            };
            ws.send(JSON.stringify(messageWithId));
            console.log("📤 Sent:", messageWithId.type);
            return true;
        }
        console.warn("⚠️ WebSocket not connected, message not sent");
        return false;
    }, []);

    const subscribe = useCallback((handler: SocketMessageHandler) => {
        subscribersRef.current.add(handler);
        return () => {
            subscribersRef.current.delete(handler);
        };
    }, []);

    const clearMessages = useCallback(() => {
        setMessages([]);
        messageIdsRef.current.clear();
    }, []);

    const reconnect = useCallback(() => {
        reconnectAttemptsRef.current = 0;
        setReconnectAttempts(0);
        manualCloseRef.current = false;
        const ws = sharedWs ?? socketRef.current;
        if (ws) {
            ws.onclose = null;
            ws.onerror = null;
            try {
                ws.close();
            } catch {
                /* ignore */
            }
            if (sharedWs === ws) sharedWs = null;
            if (socketRef.current === ws) socketRef.current = null;
        }
        connect();
    }, [connect]);

    return {
        isConnected,
        reconnectAttempts: reconnectCount,
        reconnectExhausted:
            !isConnected && reconnectCount >= maxReconnectAttempts,
        maxReconnectAttempts,
        messages,
        sendMessage,
        subscribe,
        clearMessages,
        reconnect,
    };
};

export default useStreamingSocket;
